// Mock implementation of IBillingMockService backed by static scenario fixtures.
// See plans/saas-billing/01-stripe-billing-foundation.md.

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';

import {
  AICreditBalance,
  AppliedCoupon,
  BillingPortalLink,
  CancellationError,
  CancellationInput,
  CancellationPreview,
  CancellationReason,
  ConsumptionEvent,
  CountryCode,
  CurrencyCode,
  EnforcementLevel,
  EnforcementSeverity,
  EnforcementStatus,
  FeatureUsageBreakdown,
  Invoice,
  PaymentMethod,
  Plan,
  PlanChangeError,
  PlanChangeInput,
  PlanChangePreview,
  PlanChangeWarning,
  PricePoint,
  ProrationLine,
  ReactivateError,
  Receipt,
  RestrictedFeature,
  RetentionOffer,
  RetentionOfferError,
  RetentionOfferType,
  Subscription,
  TaxBasis,
  TaxIdError,
  TaxIdInput,
  TaxIdRecord,
  TaxPreview,
  TaxPreviewInput,
  TopUpCheckoutError,
  TopUpHistoryEntry,
  TopUpPack,
  TopUpSuccess
} from '../../models/billing';
import {
  CurrentUser,
  IBillingMockService,
  PromotionCodeError,
  ScenarioName,
  UserRole
} from './billing-mock.interface';
import { SCENARIOS, Scenario } from './fixtures';

// Random latency window in ms to surface loading-state UX bugs early.
const MIN_DELAY_MS = 150;
const MAX_DELAY_MS = 400;

function randomDelayMs(): number {
  return MIN_DELAY_MS + Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS);
}

// 30-day cycle assumption used by the deterministic proration ratio (#04).
// The spec pins the canonical demo to "16 days remaining of a 30-day cycle"
// regardless of the actual fixture dates so the math in the wireframe and the
// math in the prototype are guaranteed to match. We keep both the denominator
// and the "remaining days" as constants to make the ratio explicit; real impl
// will derive these from Stripe's upcoming-invoice proration_date.
const PRORATION_CYCLE_DAYS = 30;
const PRORATION_REMAINING_DAYS = 16;
const PRORATION_RATIO = PRORATION_REMAINING_DAYS / PRORATION_CYCLE_DAYS;

// Map known tier codes to a friendly display name for proration line copy.
// We do not import shared-plans Plan[] here since each scenario carries its
// own plans list; for the prototype we only need the display label.
const TIER_DISPLAY_NAME: Record<string, string> = {
  FOUNDATION: 'Foundation',
  PROFESSIONAL: 'Professional',
  BUSINESS: 'Business'
};

function hashPlanChangeInput(input: PlanChangeInput, scenario: ScenarioName): string {
  // Cheap deterministic hash. Real impl uses HMAC; for the prototype we just
  // need a value that changes when any of the input fields change so commit
  // can detect mismatched inputs.
  return [scenario, input.targetTier, input.targetSeats, input.targetBillingPeriod].join('|');
}

function shortHex(length: number): string {
  let s = '';
  while (s.length < length) {
    s += Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0');
  }
  return s.slice(0, length);
}

// Module-level retention-offer rate-limit map (1 offer per tenant per 12 months).
// Lives at module scope (not on the class) so the mock's "server-side" gating
// survives DI re-instantiation in unit tests for a single Jasmine run; tests
// that need a clean state call `service.__resetRetentionRateLimit()` in beforeEach.
// In a real backend this is persisted; for the prototype we keep it lightweight.
const RETENTION_OFFER_RATE_LIMIT_MAP = new Map<string, number>();
const RETENTION_OFFER_WINDOW_MS = 365 * 24 * 60 * 60 * 1000; // 12 months
// 30-day cycle assumption for the past_due endOfPeriod fallback. Stripe
// preserves the prior paid cycle when a renewal charge fails; we mirror that
// by subtracting one cycle length from pastDueSince. Real impl reads the
// actual prior cycle end from the persisted subscription history.
const PAST_DUE_CYCLE_FALLBACK_MS = 30 * 24 * 60 * 60 * 1000;
// 30-day data retention copy in step 3 (per spec). Surfaced via the preview so
// the UI can render the exact number rather than hardcode.
const DATA_RETENTION_DAYS = 30;

// #10 — Stripe Tax: per-country rate table. Values are illustrative SMB
// reference rates, not legal advice. See plans/saas-billing/10-stripe-tax.md.
interface TaxRateConfig {
  rate: number;
  basis: 'inclusive' | 'exclusive';
  label: string;       // "VAT" or "Sales tax"
  jurisdiction: string; // "CA sales 8.25%" etc.
}

const TAX_RATE_TABLE: Record<string, TaxRateConfig> = {
  US: { rate: 0.0825, basis: 'exclusive', label: 'Sales tax', jurisdiction: 'CA sales 8.25%' },
  GB: { rate: 0.20,   basis: 'exclusive', label: 'VAT',       jurisdiction: 'UK VAT 20%' },
  NG: { rate: 0.075,  basis: 'inclusive', label: 'VAT',       jurisdiction: 'NG VAT 7.5%' },
  ZA: { rate: 0.15,   basis: 'inclusive', label: 'VAT',       jurisdiction: 'ZA VAT 15%' },
  GH: { rate: 0.125,  basis: 'inclusive', label: 'VAT',       jurisdiction: 'GH VAT 12.5%' },
  KE: { rate: 0.16,   basis: 'inclusive', label: 'VAT',       jurisdiction: 'KE VAT 16%' },
  DE: { rate: 0.19,   basis: 'exclusive', label: 'VAT',       jurisdiction: 'DE VAT 19%' },
  FR: { rate: 0.20,   basis: 'exclusive', label: 'VAT',       jurisdiction: 'FR VAT 20%' },
  IE: { rate: 0.23,   basis: 'exclusive', label: 'VAT',       jurisdiction: 'IE VAT 23%' }
};

// Countries eligible for reverse-charge when the customer supplies a valid
// VAT ID from the same country. Includes GB B2B per #10 spec.
const REVERSE_CHARGE_COUNTRIES: ReadonlySet<string> = new Set([
  'GB', 'DE', 'FR', 'IE'
]);

// Validation regexes per tax ID type. These are intentionally simple - real
// impl calls Stripe Tax ID validation API.
const TAX_ID_VALIDATORS: Record<string, RegExp> = {
  gb_vat: /^GB\d{9}$/i,
  eu_vat: /^[A-Z]{2}\d{8,12}$/i,
  ng_tin: /^\d{8}-\d{4}$/,
  za_vat: /^4\d{9}$/,
  gh_tin: /^[A-Z]\d{10}$/i,
  ke_pin: /^[AP]\d{9}[A-Z]$/i,
  us_ein: /^\d{2}-\d{7}$/
};

// Banker's rounding (round-half-to-even). Used for inclusive-tax math per
// the #10 decisions log to match Stripe's rounding policy.
function bankerRound(value: number): number {
  const rounded = Math.round(value);
  // Math.round rounds half UP (away from zero for positives). For halves we
  // need to round to even.
  if (Math.abs(value - Math.trunc(value)) === 0.5) {
    const floor = Math.floor(value);
    return floor % 2 === 0 ? floor : floor + 1;
  }
  return rounded;
}

@Injectable({ providedIn: 'root' })
export class BillingMockService implements IBillingMockService {
  private readonly activeScenario$ = new BehaviorSubject<ScenarioName>('healthy_monthly_us');

  // Per-scenario subscription overrides. When applyPromotionCode /
  // removeActivePromotionCode mutate the active subscription, we store the
  // updated subscription here and emit on `activeScenarioChanges$` so any
  // currently-rendered billing screen re-fetches and sees the new state.
  // The original static fixtures in `SCENARIOS` are never mutated, so unit
  // tests can rely on stable fixture data per test through __setScenario.
  private readonly subscriptionOverrides = new Map<ScenarioName, Subscription | null>();

  // #08: per-scenario AICreditBalance overrides. Mutated by
  // __simulateAIConsumption + __resetAICreditCycle so reviewers can demo
  // threshold toasts without picker hops. Cleared on __setScenario alongside
  // the subscription overrides.
  private readonly aiCreditBalanceOverrides = new Map<ScenarioName, AICreditBalance | null>();

  // #09: per-scenario receipts/top-up-history overrides. Mutated when
  // simulateTopUpCompletion succeeds so the new purchase shows up in #08's
  // history list + #03's receipts card without re-rendering the fixture.
  // Cleared on __setScenario alongside the other overrides.
  private readonly receiptsOverrides = new Map<ScenarioName, Receipt[]>();
  private readonly topUpHistoryOverrides = new Map<ScenarioName, TopUpHistoryEntry[]>();

  // #09: in-flight top-up checkout sessions. Created by initiateTopUpCheckout,
  // consumed by simulateTopUpCompletion. Sessions are scenario-scoped so a
  // session created in one scenario can't accidentally complete against a
  // different scenario after a picker swap.
  private readonly inFlightTopUpSessions = new Map<
    string,
    { packId: string; currency: CurrencyCode; countryCode: CountryCode; scenarioName: ScenarioName }
  >();

  // #09: dev-only escape hatch. Arms the NEXT simulateTopUpCompletion to
  // return STRIPE_DECLINED and clears itself after one consumption.
  private simulateTopUpFailureOnce = false;

  // Issued preview tokens for change-plan flows. Maps token -> input hash so
  // commitPlanChange can detect a stale or tampered-with preview before
  // mutating the subscription.
  private readonly issuedPreviewTokens = new Map<string, string>();

  // Dev-only escape hatches: when set, the NEXT commitPlanChange call returns
  // the corresponding error and clears the flag. Wired through the scenario
  // picker's "Simulate stale preview" / "Simulate card decline" checkboxes.
  private simulateStalePreviewOnce = false;
  private simulateStalePreviewWithDiffOnce = false;
  private simulateCardDeclineOnce = false;
  // When `simulateStalePreviewWithDiffOnce` fires, the *next* previewPlanChange
  // call (the silent-recovery refresh after PREVIEW_STALE) shifts totalMinor by
  // exactly $1.00 (100 minor units) so the UI's > $0.01 delta branch triggers
  // and bounces back to step 4 with the stale-notice banner.
  private nextPreviewShiftMinor = 0;

  // #10 — Per-tenant tax ID records. Keyed by tenantId so a picker swap
  // between scenarios with different tenants doesn't bleed IDs. Seeded with
  // fixtures matching the reverse_charge_de + cancelled_grace_period_gb
  // scenarios for the Pattern C demo.
  private readonly taxIdsByTenant = new Map<string, TaxIdRecord[]>();
  private taxIdsSeeded = false;

  // Exposed as an Observable so screens can re-fetch when the scenario picker
  // switches OR when an applied coupon changes. Per the prototype acceptance
  // criteria (#01) all visible billing screens should re-render within 500ms
  // of a switch.
  readonly activeScenarioChanges$: Observable<ScenarioName> = this.activeScenario$.asObservable();

  // ---------------------------------------------------------------------------
  // #06 — Payment enforcement & dunning state.
  // currentUser$ drives the non-admin banner variant. paymentRecoveryEvents$
  // emits once per __simulatePaymentRecovery() call so the app shell can show
  // the post-recovery toast without polling.
  // ---------------------------------------------------------------------------
  private readonly currentUser$ = new BehaviorSubject<CurrentUser>({
    id: 'user-1',
    canManageBilling: true
  });
  private readonly paymentRecoveryEventsSubject = new Subject<void>();
  readonly paymentRecoveryEvents$: Observable<void> =
    this.paymentRecoveryEventsSubject.asObservable();

  getCurrentSubscription(): Observable<Subscription | null> {
    return this.withScenario(scenario => this.resolveSubscription(scenario));
  }

  getAvailablePlans(countryCode: CountryCode, currency: CurrencyCode): Observable<Plan[]> {
    return this.withScenario(scenario => {
      // Filter the plan list down to pricing entries that match the requested
      // country + currency, but keep all 3 tiers (FOUNDATION/PROFESSIONAL/BUSINESS).
      return scenario.plans
        .map(plan => ({
          ...plan,
          pricing: plan.pricing.filter(
            p => p.countryCode === countryCode && p.currency === currency
          )
        }))
        .filter(plan => plan.pricing.length > 0);
    });
  }

  listInvoices(limit?: number): Observable<Invoice[]> {
    return this.withScenario(scenario => {
      const items = scenario.invoices;
      return typeof limit === 'number' ? items.slice(0, limit) : items;
    });
  }

  listReceipts(limit?: number): Observable<Receipt[]> {
    return this.withScenario(scenario => {
      // #09: prefer the override list when a top-up has appended new receipts;
      // fall back to the fixture baseline otherwise.
      const items = this.receiptsOverrides.get(scenario.name) ?? scenario.receipts;
      return typeof limit === 'number' ? items.slice(0, limit) : items;
    });
  }

  createPortalSession(returnUrl: string): Observable<BillingPortalLink> {
    return this.withScenario(() => {
      const id = Math.random().toString(36).slice(2, 12);
      const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString(); // now + 1h
      return {
        url: `https://billing.stripe.com/p/session/mock_${id}`,
        expiresAt,
        returnUrl
      } as BillingPortalLink;
    });
  }

  getDefaultPaymentMethod(): Observable<PaymentMethod | null> {
    return this.withScenario(scenario => scenario.paymentMethod);
  }

  validatePromotionCode(code: string): Observable<AppliedCoupon | { error: PromotionCodeError }> {
    // Preview path: resolves against the current subscription's coupon state
    // but does NOT mutate it.
    return this.withScenario(scenario => {
      const subscription = this.resolveSubscription(scenario);
      return this.computePromotionCodeResult(subscription, code, { isPreview: true });
    });
  }

  applyPromotionCode(
    code: string
  ): Observable<AppliedCoupon | { error: PromotionCodeError }> {
    // Commit path: returns the same matching/error logic as
    // validatePromotionCode but on success also updates the active scenario's
    // subscription override and re-emits on activeScenarioChanges$ so visible
    // screens re-fetch.
    return this.withScenario(scenario => {
      const subscription = this.resolveSubscription(scenario);
      const result = this.computePromotionCodeResult(subscription, code, {
        isPreview: false
      });

      if (this.isPromoError(result)) {
        return result;
      }

      // Commit onto the active subscription. No subscription = nothing to bind.
      if (subscription !== null) {
        const updated = this.applyCouponToSubscription(subscription, result);
        this.setSubscriptionOverride(scenario.name, updated);
      }
      return result;
    });
  }

  removeActivePromotionCode(): Observable<void> {
    return this.withScenario(scenario => {
      const subscription = this.resolveSubscription(scenario);
      if (subscription === null || subscription.appliedCoupon === null) {
        // No-op: nothing to remove.
        return undefined;
      }
      const updated = this.clearCouponFromSubscription(subscription);
      this.setSubscriptionOverride(scenario.name, updated);
      return undefined;
    });
  }

  getAICreditBalance(): Observable<AICreditBalance | null> {
    return this.withScenario(scenario => this.resolveAICreditBalance(scenario));
  }

  listTopUpPacks(currency: CurrencyCode): Observable<TopUpPack[]> {
    return this.withScenario(scenario =>
      scenario.topUpPacks
        .map(pack => ({
          ...pack,
          pricing: pack.pricing.filter(p => p.currency === currency)
        }))
        .filter(pack => pack.pricing.length > 0)
    );
  }

  // ---------------------------------------------------------------------------
  // #09 — AI credit top-up checkout
  // ---------------------------------------------------------------------------

  initiateTopUpCheckout(
    packId: string,
    currency: CurrencyCode,
    countryCode: CountryCode
  ): Observable<{ checkoutSessionId: string; checkoutUrl: string } | { error: TopUpCheckoutError }> {
    return this.withScenario(scenario => {
      const pack = scenario.topUpPacks.find(p => p.id === packId);
      if (pack === undefined) {
        return { error: 'PACK_NOT_FOUND' as TopUpCheckoutError };
      }
      const pricePoint = pack.pricing.find(
        p => p.currency === currency && p.countryCode === countryCode
      );
      if (pricePoint === undefined) {
        return { error: 'PRICE_NOT_AVAILABLE_FOR_CURRENCY' as TopUpCheckoutError };
      }
      const checkoutSessionId = `mock_cs_${shortHex(16)}`;
      this.inFlightTopUpSessions.set(checkoutSessionId, {
        packId,
        currency,
        countryCode,
        scenarioName: scenario.name
      });
      return {
        checkoutSessionId,
        checkoutUrl: `https://checkout.stripe.com/mock/${checkoutSessionId}`
      };
    });
  }

  simulateTopUpCompletion(
    checkoutSessionId: string
  ): Observable<TopUpSuccess | { error: TopUpCheckoutError }> {
    return this.withScenario(scenario => {
      // Validate session id format up front: must be a mock_cs_* token issued
      // by initiateTopUpCheckout. Anything else is treated as invalid so the
      // UI surfaces the dedicated error rather than failing silently.
      if (!checkoutSessionId.startsWith('mock_cs_')) {
        return { error: 'CHECKOUT_SESSION_INVALID' as TopUpCheckoutError };
      }
      const session = this.inFlightTopUpSessions.get(checkoutSessionId);
      if (session === undefined) {
        return { error: 'CHECKOUT_SESSION_INVALID' as TopUpCheckoutError };
      }

      // Dev escape hatch: declined. Burn the session and clear the flag so a
      // retry from the failure state can succeed cleanly.
      if (this.simulateTopUpFailureOnce) {
        this.simulateTopUpFailureOnce = false;
        this.inFlightTopUpSessions.delete(checkoutSessionId);
        return { error: 'STRIPE_DECLINED' as TopUpCheckoutError };
      }

      const pack = scenario.topUpPacks.find(p => p.id === session.packId);
      if (pack === undefined) {
        this.inFlightTopUpSessions.delete(checkoutSessionId);
        return { error: 'PACK_NOT_FOUND' as TopUpCheckoutError };
      }
      const pricePoint = pack.pricing.find(
        p => p.currency === session.currency && p.countryCode === session.countryCode
      );
      if (pricePoint === undefined) {
        this.inFlightTopUpSessions.delete(checkoutSessionId);
        return { error: 'PRICE_NOT_AVAILABLE_FOR_CURRENCY' as TopUpCheckoutError };
      }

      // Burn the session so a double-tap on the success state can't reapply.
      this.inFlightTopUpSessions.delete(checkoutSessionId);

      // Increment the active scenario's top-up balance via the override map so
      // #08's gauge re-renders. Fall back to fixture baseline when no override
      // exists yet.
      const balanceBefore = this.resolveAICreditBalance(scenario);
      let newTopUpBalance = pack.credits;
      if (balanceBefore !== null) {
        newTopUpBalance = balanceBefore.topUpBalance + pack.credits;
        const updatedBalance: AICreditBalance = {
          ...balanceBefore,
          topUpBalance: newTopUpBalance,
          remainingCredits:
            balanceBefore.allocatedCredits -
            balanceBefore.usedCredits -
            balanceBefore.reservedCredits +
            newTopUpBalance
        };
        this.aiCreditBalanceOverrides.set(scenario.name, updatedBalance);
      }

      // Append a TopUpHistoryEntry so the new purchase shows up on /billing/credits.
      const purchasedAtIso = new Date().toISOString();
      const topUpHistoryId = `tu_${shortHex(12)}`;
      const historyEntry: TopUpHistoryEntry = {
        id: topUpHistoryId,
        creditTopUpId: topUpHistoryId,
        purchasedAt: purchasedAtIso,
        packDisplayName: pack.displayName,
        credits: pack.credits,
        amountMinor: pricePoint.amountMinor,
        currency: pricePoint.currency,
        remaining: pack.credits,
        status: 'completed'
      };
      const existingHistory =
        this.topUpHistoryOverrides.get(scenario.name) ?? (scenario.topUpHistory ?? []).slice();
      this.topUpHistoryOverrides.set(scenario.name, [historyEntry, ...existingHistory]);

      // Append a Receipt to the active scenario's receipts list (head of list)
      // so #03's "Recent receipts" card surfaces the new purchase too.
      const newReceipt: Receipt = {
        id: `rcpt_${shortHex(10)}`,
        chargedAt: purchasedAtIso,
        amountMinor: pricePoint.amountMinor,
        currency: pricePoint.currency,
        description: `AI credit top-up: ${pack.displayName}`,
        invoiceId: null,
        topUpPackId: pack.id
      };
      const existingReceipts =
        this.receiptsOverrides.get(scenario.name) ?? scenario.receipts.slice();
      this.receiptsOverrides.set(scenario.name, [newReceipt, ...existingReceipts]);

      const result: TopUpSuccess = {
        pack: { ...pack, pricing: [pricePoint] },
        pricePoint,
        newTopUpBalance,
        receiptUrl: `https://receipt.stripe.com/mock/${newReceipt.id}`,
        checkoutSessionId
      };

      // Re-emit on the scenario stream so #08's gauge + the credits detail
      // page re-fetch the new balance + history.
      this.activeScenario$.next(scenario.name);
      return result;
    });
  }

  __simulateTopUpFailure(active: boolean): void {
    this.simulateTopUpFailureOnce = active;
  }

  // ---------------------------------------------------------------------------
  // #08 — AI credit usage display
  // ---------------------------------------------------------------------------

  listRecentConsumption(limit?: number): Observable<ConsumptionEvent[]> {
    return this.withScenario(scenario => {
      const events = scenario.consumptionEvents ?? [];
      return typeof limit === 'number' ? events.slice(0, limit) : events.slice();
    });
  }

  getFeatureUsageBreakdown(): Observable<FeatureUsageBreakdown[]> {
    return this.withScenario(scenario => {
      const rows = scenario.featureUsageBreakdown ?? [];
      return rows.slice();
    });
  }

  listTopUpHistory(limit?: number): Observable<TopUpHistoryEntry[]> {
    return this.withScenario(scenario => {
      // #09: prefer the override list when a top-up has appended new entries.
      const history =
        this.topUpHistoryOverrides.get(scenario.name) ?? scenario.topUpHistory ?? [];
      return typeof limit === 'number' ? history.slice(0, limit) : history.slice();
    });
  }

  __simulateAIConsumption(amountCredits: number): void {
    const scenarioName = this.activeScenario$.value;
    const scenario = SCENARIOS[scenarioName];
    const baseline = this.resolveAICreditBalance(scenario);
    if (baseline === null || amountCredits <= 0) {
      return;
    }
    const usedNext = Math.min(baseline.allocatedCredits, baseline.usedCredits + amountCredits);
    const remaining = baseline.allocatedCredits - usedNext + baseline.topUpBalance;
    const updated: AICreditBalance = {
      ...baseline,
      usedCredits: usedNext,
      remainingCredits: Math.max(0, remaining)
    };
    this.aiCreditBalanceOverrides.set(scenarioName, updated);
    // Re-emit so subscribers re-fetch the new balance.
    this.activeScenario$.next(scenarioName);
  }

  __resetAICreditCycle(): void {
    const scenarioName = this.activeScenario$.value;
    const scenario = SCENARIOS[scenarioName];
    if (scenario.aiCreditBalance === null) {
      return;
    }
    // Override usedCredits to 0 from the fixture baseline.
    const reset: AICreditBalance = {
      ...scenario.aiCreditBalance,
      usedCredits: 0,
      remainingCredits:
        scenario.aiCreditBalance.allocatedCredits +
        scenario.aiCreditBalance.topUpBalance
    };
    this.aiCreditBalanceOverrides.set(scenarioName, reset);
    // Clear toast dedup keys so threshold toasts can re-fire.
    if (typeof sessionStorage !== 'undefined') {
      const prefix = `${scenario.aiCreditBalance.tenantId}:${scenario.aiCreditBalance.billingCycleStart}:`;
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key !== null && key.startsWith(`solo_ai_credit_toast:${prefix}`)) {
          keysToRemove.push(key);
        }
      }
      for (const key of keysToRemove) {
        sessionStorage.removeItem(key);
      }
    }
    this.activeScenario$.next(scenarioName);
  }

  previewPlanChange(
    input: PlanChangeInput
  ): Observable<PlanChangePreview | { error: PlanChangeError }> {
    return this.withScenario(scenario => {
      const subscription = this.resolveSubscription(scenario);
      if (subscription === null) {
        // No active subscription = nothing to change. Reuse NO_OP since the
        // UI shouldn't be calling preview without a subscription.
        return { error: 'NO_OP' as PlanChangeError };
      }

      // No-op guard: selection matches current subscription exactly.
      if (
        input.targetTier === subscription.tier &&
        input.targetSeats === subscription.seats &&
        input.targetBillingPeriod === subscription.billingPeriod
      ) {
        return { error: 'NO_OP' as PlanChangeError };
      }

      const targetPricePoint = this.findPricePoint(
        scenario.plans,
        input.targetTier,
        subscription.countryCode,
        subscription.currency,
        input.targetBillingPeriod
      );
      if (targetPricePoint === null) {
        // No matching price point for the requested combination - bail.
        return { error: 'NO_OP' as PlanChangeError };
      }

      const currentSubtotalMinor = subscription.subtotalMinor;
      const newSubtotalMinor = targetPricePoint.perSeatMinor * input.targetSeats;

      // Compute proration credit + charge using the canonical 16/30 ratio.
      // Credit is the unused portion of the current subscription; charge is the
      // prorated cost of the new subscription for the remainder of the cycle.
      const creditMinor = -Math.round(PRORATION_RATIO * currentSubtotalMinor);
      const chargeMinor = Math.round(PRORATION_RATIO * newSubtotalMinor);

      const effectiveDateIso = new Date().toISOString();
      const nextRegularChargeAt = subscription.currentPeriodEnd;

      const currentTierLabel = TIER_DISPLAY_NAME[subscription.tier] ?? subscription.tier;
      const targetTierLabel = TIER_DISPLAY_NAME[input.targetTier] ?? input.targetTier;

      const prorationLines: ProrationLine[] = [
        {
          description: `Credit for unused ${currentTierLabel} × ${subscription.seats}`,
          amountMinor: creditMinor,
          periodStart: effectiveDateIso,
          periodEnd: nextRegularChargeAt
        },
        {
          description: `${targetTierLabel} × ${input.targetSeats} prorated`,
          amountMinor: chargeMinor,
          periodStart: effectiveDateIso,
          periodEnd: nextRegularChargeAt
        }
      ];

      const subtotalMinor = creditMinor + chargeMinor;

      // Apply existing coupon discount on a percent basis if present. Coupons
      // only apply to positive subtotals (negative subtotals already accrue
      // account credit; discounting an already-negative number makes no sense
      // and Stripe ignores it in practice).
      let discountMinor = 0;
      const coupon = subscription.appliedCoupon;
      if (coupon !== null && subtotalMinor > 0) {
        if (coupon.discountType === 'percent' && coupon.percentOff !== null) {
          discountMinor = Math.round(subtotalMinor * (coupon.percentOff / 100));
        } else if (coupon.discountType === 'fixed' && coupon.amountOffMinor !== null) {
          discountMinor = Math.min(coupon.amountOffMinor, subtotalMinor);
        }
      }

      const rawTotal = subtotalMinor - discountMinor;
      let totalMinor = rawTotal;
      let accountCreditAppliedMinor = 0;
      if (rawTotal <= 0) {
        accountCreditAppliedMinor = -rawTotal;
        totalMinor = 0;
      }

      // Compute warnings.
      const warnings: PlanChangeWarning[] = [];

      // USAGE_EXCEEDS_NEW_ALLOCATION: when downgrading to a tier whose
      // (aiCreditsPerSeat * targetSeats) is less than the current usedCredits.
      const balance = scenario.aiCreditBalance;
      if (balance !== null) {
        const newAllocation = targetPricePoint.aiCreditsPerSeat * input.targetSeats;
        if (balance.usedCredits > newAllocation) {
          warnings.push({
            code: 'USAGE_EXCEEDS_NEW_ALLOCATION',
            usedCredits: balance.usedCredits,
            newAllocation
          });
        }
      }

      // SEATS_BELOW_ACTIVE_USERS: when targetSeats < activeUserCount fixture.
      const activeUsers = scenario.activeUserCount ?? subscription.seats;
      if (input.targetSeats < activeUsers) {
        warnings.push({
          code: 'SEATS_BELOW_ACTIVE_USERS',
          activeUsers,
          newSeats: input.targetSeats
        });
      }

      // Period switch warnings.
      if (
        subscription.billingPeriod === 'monthly' &&
        input.targetBillingPeriod === 'annual'
      ) {
        const firstAnnualChargeMinor = newSubtotalMinor; // full annual subtotal
        // Annual renewal is one year out from "today".
        const nextRenewalAt = new Date(
          Date.now() + 365 * 24 * 60 * 60 * 1000
        ).toISOString();
        warnings.push({
          code: 'SWITCHING_TO_ANNUAL',
          firstAnnualChargeMinor,
          nextRenewalAt
        });
      } else if (
        subscription.billingPeriod === 'annual' &&
        input.targetBillingPeriod === 'monthly'
      ) {
        warnings.push({
          code: 'SWITCHING_TO_MONTHLY',
          accountCreditAppliedMinor
        });
      }

      // Apply a one-shot total shift requested by
      // __simulateStalePreviewWithDiff. This runs ONCE per arming - the second
      // preview call after `simulateStalePreviewWithDiffOnce` fires - so that
      // the recovery-refresh preview differs from the original by > $0.01.
      // We add to totalMinor (not subtotal) so the UI's threshold check fires
      // off whichever value it compares.
      if (this.nextPreviewShiftMinor !== 0) {
        const shift = this.nextPreviewShiftMinor;
        this.nextPreviewShiftMinor = 0;
        totalMinor = totalMinor + shift;
      }

      const previewToken = shortHex(16);
      this.issuedPreviewTokens.set(
        previewToken,
        hashPlanChangeInput(input, scenario.name)
      );

      const preview: PlanChangePreview = {
        previewToken,
        currentSubtotalMinor,
        newSubtotalMinor,
        prorationLines,
        subtotalMinor,
        discountMinor,
        totalMinor,
        accountCreditAppliedMinor,
        effectiveDate: effectiveDateIso,
        nextRegularChargeAt,
        nextRegularChargeMinor: newSubtotalMinor,
        currency: subscription.currency,
        warnings,
        resolvedInput: input
      };
      return preview;
    });
  }

  commitPlanChange(
    input: PlanChangeInput,
    previewToken: string
  ): Observable<Subscription | { error: PlanChangeError }> {
    return this.withScenario(scenario => {
      // Dev escape hatches: card decline first (truer to user experience -
      // backend would attempt the change, then the charge would fail).
      if (this.simulateCardDeclineOnce) {
        this.simulateCardDeclineOnce = false;
        return { error: 'CARD_DECLINED' as PlanChangeError };
      }
      if (this.simulateStalePreviewOnce) {
        this.simulateStalePreviewOnce = false;
        // Invalidate the supplied token so the recovery path is fully
        // exercised; if the UI re-previews it will get a new token.
        this.issuedPreviewTokens.delete(previewToken);
        return { error: 'PREVIEW_STALE' as PlanChangeError };
      }
      if (this.simulateStalePreviewWithDiffOnce) {
        this.simulateStalePreviewWithDiffOnce = false;
        // Arm the NEXT previewPlanChange (the silent recovery refresh) to
        // return totals shifted by exactly $1.00 so the UI's > $0.01 delta
        // branch triggers and bounces back to step 4 with the stale notice.
        this.nextPreviewShiftMinor = 100;
        this.issuedPreviewTokens.delete(previewToken);
        return { error: 'PREVIEW_STALE' as PlanChangeError };
      }

      const subscription = this.resolveSubscription(scenario);
      if (subscription === null) {
        return { error: 'NO_OP' as PlanChangeError };
      }

      // Token must exist AND its bound input hash must match the supplied
      // input. This prevents previewing one shape and committing another by
      // tampering with the request.
      const expectedHash = this.issuedPreviewTokens.get(previewToken);
      const actualHash = hashPlanChangeInput(input, scenario.name);
      if (expectedHash === undefined || expectedHash !== actualHash) {
        return { error: 'PREVIEW_STALE' as PlanChangeError };
      }

      // No-op defense in depth (UI should have prevented reaching here).
      if (
        input.targetTier === subscription.tier &&
        input.targetSeats === subscription.seats &&
        input.targetBillingPeriod === subscription.billingPeriod
      ) {
        return { error: 'NO_OP' as PlanChangeError };
      }

      const targetPricePoint = this.findPricePoint(
        scenario.plans,
        input.targetTier,
        subscription.countryCode,
        subscription.currency,
        input.targetBillingPeriod
      );
      if (targetPricePoint === null) {
        return { error: 'NO_OP' as PlanChangeError };
      }

      // Burn the token after successful commit so a second click can't double-
      // apply the change.
      this.issuedPreviewTokens.delete(previewToken);

      const newSubtotalMinor = targetPricePoint.perSeatMinor * input.targetSeats;
      let discountMinor = 0;
      const coupon = subscription.appliedCoupon;
      if (coupon !== null) {
        if (coupon.discountType === 'percent' && coupon.percentOff !== null) {
          discountMinor = Math.round(newSubtotalMinor * (coupon.percentOff / 100));
        } else if (coupon.discountType === 'fixed' && coupon.amountOffMinor !== null) {
          discountMinor = Math.min(coupon.amountOffMinor, newSubtotalMinor);
        }
      }
      const totalMinor = Math.max(0, newSubtotalMinor - discountMinor + subscription.taxMinor);

      const updated: Subscription = {
        ...subscription,
        tier: input.targetTier,
        seats: input.targetSeats,
        billingPeriod: input.targetBillingPeriod,
        pricePerSeatMinor: targetPricePoint.perSeatMinor,
        subtotalMinor: newSubtotalMinor,
        discountMinor,
        totalMinor
      };
      this.setSubscriptionOverride(scenario.name, updated);
      return updated;
    });
  }

  // ---------------------------------------------------------------------------
  // Cancel + Reactivate + Retention offers (#05)
  // ---------------------------------------------------------------------------

  previewCancellation(
    reason: CancellationReason,
    _freeText?: string
  ): Observable<CancellationPreview | { error: CancellationError }> {
    return this.withScenario(scenario => {
      const subscription = this.resolveSubscription(scenario);
      if (subscription === null) {
        return { error: 'CANCEL_NO_SUBSCRIPTION' as CancellationError };
      }
      // Already canceled (terminal) — nothing to cancel.
      if (subscription.status === 'canceled') {
        return { error: 'CANCEL_ALREADY_CANCELLED' as CancellationError };
      }
      // cancelAtPeriodEnd already set — surface the same error so callers can
      // route to reactivate instead.
      if (subscription.cancelAtPeriodEnd) {
        return { error: 'CANCEL_ALREADY_CANCELLED' as CancellationError };
      }

      const isPastDue = subscription.status === 'past_due';
      const isAnnual = subscription.billingPeriod === 'annual';

      // For past_due, endOfPeriod is the LAST PAID cycle end (already past).
      // Derive from pastDueSince - 30 days (the cycle just before the failed
      // renewal). When pastDueSince is missing, fall back to currentPeriodEnd.
      let endOfPeriod = subscription.currentPeriodEnd;
      if (isPastDue && subscription.pastDueSince !== null) {
        const lastPaidEnd = Date.parse(subscription.pastDueSince) - PAST_DUE_CYCLE_FALLBACK_MS;
        endOfPeriod = new Date(lastPaidEnd).toISOString();
      }

      // Cycle-only remaining credits (excludes top-up balance).
      const balance = scenario.aiCreditBalance;
      const cycleCreditsRemaining =
        balance !== null
          ? balance.allocatedCredits - balance.usedCredits - balance.reservedCredits
          : 0;
      const topUpBalanceAtCancel = balance !== null ? balance.topUpBalance : 0;

      // Rate-limit retention offers: 1 per tenant per 12 months.
      const tenantId = subscription.tenantId;
      const lastOfferAt = RETENTION_OFFER_RATE_LIMIT_MAP.get(tenantId);
      const rateLimited =
        lastOfferAt !== undefined && Date.now() - lastOfferAt < RETENTION_OFFER_WINDOW_MS;

      const offers = rateLimited
        ? []
        : this.computeRetentionOffers(reason, scenario, subscription);

      const preview: CancellationPreview = {
        reason,
        endOfPeriod,
        isAnnual,
        isPastDue,
        topUpBalanceAtCancel,
        cycleCreditsRemaining,
        offers,
        retentionOffersRateLimited: rateLimited
      };
      // Surface DATA_RETENTION_DAYS via a tagged comment rather than an extra
      // field — the UI hardcodes the same value for now since it never varies.
      void DATA_RETENTION_DAYS;
      return preview;
    });
  }

  commitCancellation(
    input: CancellationInput
  ): Observable<Subscription | { error: CancellationError }> {
    return this.withScenario(scenario => {
      const subscription = this.resolveSubscription(scenario);
      if (subscription === null) {
        return { error: 'CANCEL_NO_SUBSCRIPTION' as CancellationError };
      }
      if (subscription.status === 'canceled' || subscription.cancelAtPeriodEnd) {
        return { error: 'CANCEL_ALREADY_CANCELLED' as CancellationError };
      }
      // freeText soft-required for missing_feature + other. Trim and bail when
      // empty. The UI also enforces this; defense in depth.
      const trimmed = (input.freeText ?? '').trim();
      if (
        (input.reason === 'missing_feature' || input.reason === 'other') &&
        trimmed.length === 0
      ) {
        return { error: 'CANCEL_INVALID_REASON' as CancellationError };
      }

      const updated: Subscription = {
        ...subscription,
        cancelAtPeriodEnd: true,
        canceledAt: new Date().toISOString()
        // Note: status stays 'active' per Stripe semantics until the period
        // actually ends. Top-up forfeit is bookkeeping at endOfPeriod; the
        // wallet balance is left as-is in the mock since the forfeit is
        // future-dated.
      };
      this.setSubscriptionOverride(scenario.name, updated);
      return updated;
    });
  }

  reactivate(): Observable<Subscription | { error: ReactivateError }> {
    return this.withScenario(scenario => {
      const subscription = this.resolveSubscription(scenario);
      if (subscription === null) {
        return { error: 'NOT_CANCELLED' as ReactivateError };
      }
      // Terminal canceled = grace expired (Stripe transitions to terminal at
      // period end and we can't un-cancel). The UI routes to /signup.
      if (subscription.status === 'canceled') {
        return { error: 'GRACE_EXPIRED' as ReactivateError };
      }
      if (!subscription.cancelAtPeriodEnd) {
        return { error: 'NOT_CANCELLED' as ReactivateError };
      }

      const updated: Subscription = {
        ...subscription,
        cancelAtPeriodEnd: false,
        canceledAt: null
      };
      this.setSubscriptionOverride(scenario.name, updated);
      return updated;
    });
  }

  acceptRetentionOffer(
    offerType: RetentionOfferType,
    payload: Record<string, unknown>
  ): Observable<Subscription | { error: RetentionOfferError }> {
    return this.withScenario(scenario => {
      const subscription = this.resolveSubscription(scenario);
      if (subscription === null) {
        return { error: 'RETENTION_OFFER_INVALID' as RetentionOfferError };
      }

      // Honor the rate limit on accept too — a tampered-with UI could try to
      // accept an offer the preview never returned. Mark the tenant before
      // mutating so a follow-up preview is rate-limited.
      const tenantId = subscription.tenantId;

      // Defense in depth: refuse the offer accept if the tenant is still
      // within the 12-month retention-offer window. Caller may have bypassed
      // the previewCancellation rate-limit gate (e.g., tampered client) or
      // raced two accepts concurrently. Mirrors the gate in previewCancellation.
      const lastOfferAt = RETENTION_OFFER_RATE_LIMIT_MAP.get(tenantId);
      if (
        lastOfferAt !== undefined &&
        Date.now() - lastOfferAt < RETENTION_OFFER_WINDOW_MS
      ) {
        return { error: 'RETENTION_OFFER_RATE_LIMITED' as RetentionOfferError };
      }

      if (offerType === 'discount') {
        const promotionCode =
          typeof payload['promotionCode'] === 'string' ? (payload['promotionCode'] as string) : null;
        const percentOff =
          typeof payload['percentOff'] === 'number' ? (payload['percentOff'] as number) : 25;
        const durationInMonths =
          typeof payload['durationInMonths'] === 'number'
            ? (payload['durationInMonths'] as number)
            : 3;
        if (promotionCode === null) {
          return { error: 'RETENTION_OFFER_INVALID' as RetentionOfferError };
        }
        const nowIso = new Date().toISOString();
        const expiresAt = new Date(
          Date.now() + durationInMonths * 30 * 24 * 60 * 60 * 1000
        ).toISOString();
        const coupon: AppliedCoupon = {
          promotionCode,
          couponId: `coupon_${promotionCode.toLowerCase()}`,
          name: `${percentOff}% off for ${durationInMonths} months`,
          discountType: 'percent',
          percentOff,
          amountOffMinor: null,
          duration: 'repeating',
          durationInMonths,
          appliedAt: nowIso,
          expiresAt
        };
        const updated = this.applyCouponToSubscription(subscription, coupon);
        RETENTION_OFFER_RATE_LIMIT_MAP.set(tenantId, Date.now());
        this.setSubscriptionOverride(scenario.name, updated);
        return updated;
      }

      if (offerType === 'downgrade') {
        const newTier =
          typeof payload['newTier'] === 'string' ? (payload['newTier'] as string) : 'FOUNDATION';
        const newSeats =
          typeof payload['newSeats'] === 'number'
            ? (payload['newSeats'] as number)
            : subscription.seats;
        const pricePoint = this.findPricePoint(
          scenario.plans,
          newTier,
          subscription.countryCode,
          subscription.currency,
          subscription.billingPeriod
        );
        if (pricePoint === null) {
          return { error: 'RETENTION_OFFER_INVALID' as RetentionOfferError };
        }
        const newSubtotal = pricePoint.perSeatMinor * newSeats;
        let discount = 0;
        const coupon = subscription.appliedCoupon;
        if (coupon !== null) {
          if (coupon.discountType === 'percent' && coupon.percentOff !== null) {
            discount = Math.round(newSubtotal * (coupon.percentOff / 100));
          } else if (coupon.discountType === 'fixed' && coupon.amountOffMinor !== null) {
            discount = Math.min(coupon.amountOffMinor, newSubtotal);
          }
        }
        const updated: Subscription = {
          ...subscription,
          tier: newTier as Subscription['tier'],
          seats: newSeats,
          pricePerSeatMinor: pricePoint.perSeatMinor,
          subtotalMinor: newSubtotal,
          discountMinor: discount,
          totalMinor: Math.max(0, newSubtotal - discount + subscription.taxMinor)
        };
        RETENTION_OFFER_RATE_LIMIT_MAP.set(tenantId, Date.now());
        this.setSubscriptionOverride(scenario.name, updated);
        return updated;
      }

      // pause / help_link: no subscription change but record the rate-limit
      // hit so we don't re-offer in the same 12-month window.
      RETENTION_OFFER_RATE_LIMIT_MAP.set(tenantId, Date.now());
      // Re-emit so the UI re-fetches even though the subscription is
      // unchanged (keeps the cancel modal close + toast paths uniform).
      this.setSubscriptionOverride(scenario.name, subscription);
      return subscription;
    });
  }

  private computeRetentionOffers(
    reason: CancellationReason,
    scenario: Scenario,
    subscription: Subscription
  ): RetentionOffer[] {
    switch (reason) {
      case 'too_expensive': {
        const offers: RetentionOffer[] = [];
        // Downgrade offer (skip when already on FOUNDATION — no lower tier).
        if (subscription.tier !== 'FOUNDATION') {
          const foundationPrice = this.findPricePoint(
            scenario.plans,
            'FOUNDATION',
            subscription.countryCode,
            subscription.currency,
            subscription.billingPeriod
          );
          if (foundationPrice !== null) {
            const newSubtotal = foundationPrice.perSeatMinor * subscription.seats;
            offers.push({
              type: 'downgrade',
              headline: 'Downgrade to Foundation',
              body: `${this.tierDisplay(subscription.tier)} → Foundation at the same seat count.`,
              ctaLabel: 'Switch plan',
              payload: {
                newTier: 'FOUNDATION',
                newSeats: subscription.seats,
                newSubtotalMinor: newSubtotal,
                currency: subscription.currency
              }
            });
          }
        }
        // Auto-generated discount offer (always offered for too_expensive).
        const promotionCode = `STAY25-${shortHex(4).toUpperCase()}`;
        offers.push({
          type: 'discount',
          headline: 'Take 25% off for 3 months',
          body: 'Discount auto-applied — no code to enter.',
          ctaLabel: 'Apply offer',
          payload: {
            promotionCode,
            percentOff: 25,
            durationInMonths: 3
          }
        });
        // Cap at 2 per spec (max-2 array rule).
        return offers.slice(0, 2);
      }
      case 'not_using':
        return [
          {
            type: 'help_link',
            headline: 'Get more out of Solo',
            body: 'A 5-minute walkthrough of the features your team is missing.',
            ctaLabel: 'See getting-started',
            payload: { helpLinkUrl: '/help/getting-started' }
          }
        ];
      case 'missing_feature':
      case 'switching':
      case 'business_closed':
      case 'other':
        return [];
    }
  }

  private tierDisplay(tier: string): string {
    return TIER_DISPLAY_NAME[tier] ?? tier;
  }

  __resetRetentionRateLimit(): void {
    RETENTION_OFFER_RATE_LIMIT_MAP.clear();
  }

  // ---------------------------------------------------------------------------
  // #06 — Payment enforcement & dunning
  // ---------------------------------------------------------------------------

  getEnforcementStatus(): Observable<EnforcementStatus> {
    // Re-emit on every scenario change (and on subscription override updates,
    // which also push through activeScenario$). The combineLatest with the
    // currentUser$ subject is intentionally absent — enforcement state itself
    // doesn't depend on the user role; the banner component composes the two
    // streams in its template.
    return this.activeScenario$.pipe(
      switchMap(() =>
        of(SCENARIOS[this.activeScenario$.value]).pipe(
          delay(randomDelayMs()),
          map(scenario => this.computeEnforcementStatus(scenario))
        )
      )
    );
  }

  getCurrentUser(): Observable<CurrentUser> {
    return this.currentUser$.asObservable();
  }

  __simulatePaymentFailure(): void {
    // Flip to the freshly-failed scenario; clears overrides so the fixture's
    // baseline shape is reflected.
    this.__setScenario('past_due_us_yellow');
  }

  __simulatePaymentRecovery(): void {
    // Move back to a healthy scenario AND emit on the recovery subject so the
    // app shell can show the post-recovery toast. We emit after the scenario
    // change so subscribers see the updated enforcement state first.
    this.__setScenario('healthy_monthly_us');
    this.paymentRecoveryEventsSubject.next();
  }

  __setUserRole(role: UserRole): void {
    const current = this.currentUser$.value;
    const next: CurrentUser = {
      ...current,
      canManageBilling: role === 'billing_admin'
    };
    this.currentUser$.next(next);
  }

  private computeEnforcementStatus(scenario: Scenario): EnforcementStatus {
    const subscription = this.resolveSubscription(scenario);
    const ctaUrl = '/billing';

    // No subscription = nothing to enforce.
    if (subscription === null) {
      return { level: 'none', restrictedFeatures: [], ctaUrl };
    }

    // active / incomplete / cancelAtPeriodEnd: no #06 surface. #03 owns the
    // cancelled banner on /billing; #05 owns post-cancel UX. Cross-doc lock.
    const status = subscription.status;
    if (
      status === 'active' ||
      status === 'incomplete' ||
      status === 'incomplete_expired' ||
      status === 'canceled' ||
      status === 'paused' ||
      subscription.cancelAtPeriodEnd === true
    ) {
      return { level: 'none', restrictedFeatures: [], ctaUrl };
    }

    const allRestrictedFeatures: RestrictedFeature[] = [
      'ai_chat',
      'create_record',
      'background_jobs'
    ];

    // unpaid = Stripe exhausted retries. Always restricted.
    if (status === 'unpaid') {
      return {
        level: 'restricted',
        severity: 'red',
        daysRemaining: 0,
        ...(subscription.pastDueSince ? { pastDueSince: subscription.pastDueSince } : {}),
        ...(subscription.gracePeriodEndsAt
          ? { gracePeriodEndsAt: subscription.gracePeriodEndsAt }
          : {}),
        restrictedFeatures: allRestrictedFeatures,
        ctaUrl
      };
    }

    // past_due: severity ramp by daysRemaining; cross to restricted at day 0.
    if (status === 'past_due') {
      const daysRemaining = this.computeDaysRemaining(subscription.gracePeriodEndsAt);
      const common = {
        ...(subscription.pastDueSince ? { pastDueSince: subscription.pastDueSince } : {}),
        ...(subscription.gracePeriodEndsAt
          ? { gracePeriodEndsAt: subscription.gracePeriodEndsAt }
          : {}),
        ctaUrl
      };

      // Day-0 boundary: Solo gates even though Stripe is still retrying.
      if (daysRemaining <= 0) {
        return {
          level: 'restricted',
          severity: 'red',
          daysRemaining: 0,
          ...common,
          restrictedFeatures: allRestrictedFeatures
        };
      }

      let severity: EnforcementSeverity;
      if (daysRemaining >= 7) {
        severity = 'yellow';
      } else if (daysRemaining >= 3) {
        severity = 'orange';
      } else {
        severity = 'red';
      }

      return {
        level: 'past_due',
        severity,
        daysRemaining,
        ...common,
        restrictedFeatures: []
      };
    }

    // Fallback — unknown status. Treat as no enforcement.
    return { level: 'none', restrictedFeatures: [], ctaUrl };
  }

  private computeDaysRemaining(gracePeriodEndsAt: string | null): number {
    if (gracePeriodEndsAt === null) {
      return 0;
    }
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.max(0, Math.ceil((Date.parse(gracePeriodEndsAt) - Date.now()) / msPerDay));
  }

  // Dev-only: arm the next commitPlanChange to return PREVIEW_STALE. Used by
  // the scenario picker checkbox. Mutually exclusive with
  // __simulateStalePreviewWithDiff — arming one clears the other.
  __simulateStalePreview(active: boolean): void {
    this.simulateStalePreviewOnce = active;
    if (active) {
      this.simulateStalePreviewWithDiffOnce = false;
      this.nextPreviewShiftMinor = 0;
    }
  }

  // Dev-only: arm the next commitPlanChange to return PREVIEW_STALE AND make
  // the silent-recovery refresh return totals shifted by exactly $1.00 so the
  // UI bounces back to step 4 with the stale-notice banner visible (the
  // > $0.01 delta branch). Mutually exclusive with __simulateStalePreview.
  // Self-resets after one commit cycle (like the other simulate flags).
  __simulateStalePreviewWithDiff(active: boolean): void {
    this.simulateStalePreviewWithDiffOnce = active;
    if (active) {
      this.simulateStalePreviewOnce = false;
    } else {
      // Defensive: if turned off externally before the recovery preview
      // runs, clear the staged shift so a later unrelated preview isn't
      // perturbed.
      this.nextPreviewShiftMinor = 0;
    }
  }

  // Dev-only: arm the next commitPlanChange to return CARD_DECLINED.
  __simulateCardDecline(active: boolean): void {
    this.simulateCardDeclineOnce = active;
  }

  __setScenario(name: ScenarioName): void {
    if (this.activeScenario$.value !== name) {
      // Clear any per-scenario overrides accumulated by apply/remove so each
      // scenario starts from its fixture baseline. Without this, a coupon
      // applied during one scenario would still be present after toggling
      // back via the picker, which breaks the unit test isolation contract.
      this.subscriptionOverrides.clear();
      // #08: clear AICreditBalance overrides too so simulated consumption
      // doesn't leak across scenarios.
      this.aiCreditBalanceOverrides.clear();
      // #09: clear top-up overrides + in-flight sessions so a completed
      // purchase in one scenario doesn't leak into another via the picker.
      this.receiptsOverrides.clear();
      this.topUpHistoryOverrides.clear();
      this.inFlightTopUpSessions.clear();
      // Preview tokens are scenario-scoped; clear so a token issued under one
      // scenario can't accidentally validate against another.
      this.issuedPreviewTokens.clear();
      // Retention rate-limit is per-tenant; scenarios from different tenant
      // IDs naturally don't collide, but tests reuse the default tenant
      // across scenarios. Reset so a cancel/retention flow in scenario A
      // doesn't leak into scenario B.
      RETENTION_OFFER_RATE_LIMIT_MAP.clear();
      this.activeScenario$.next(name);
    }
  }

  __getActiveScenario(): ScenarioName {
    return this.activeScenario$.value;
  }

  // ---------------------------------------------------------------------------
  // #10 — Stripe Tax
  // ---------------------------------------------------------------------------

  previewTax(input: TaxPreviewInput): Observable<TaxPreview> {
    return of(this.computeTaxPreview(input)).pipe(delay(randomDelayMs()));
  }

  listTaxIds(): Observable<TaxIdRecord[]> {
    return this.withScenario(scenario => {
      this.ensureTaxIdsSeeded();
      const tenantId = this.resolveTenantId(scenario);
      if (tenantId === null) {
        return [] as TaxIdRecord[];
      }
      const records = this.taxIdsByTenant.get(tenantId) ?? [];
      return records.slice();
    });
  }

  addTaxId(input: TaxIdInput): Observable<TaxIdRecord | { error: TaxIdError }> {
    return this.withScenario(scenario => {
      this.ensureTaxIdsSeeded();
      const tenantId = this.resolveTenantId(scenario);
      if (tenantId === null) {
        return { error: 'UNSUPPORTED_COUNTRY' as TaxIdError };
      }
      const normalizedValue = (input.value ?? '').trim().toUpperCase().replace(/\s+/g, '');

      // Validate format FIRST so invalid values are never persisted. Previously
      // the record was stored before the format check, which meant a second
      // call with the same invalid value hit the idempotency branch and
      // returned the previously-stored invalid record without re-validating.
      // Now: invalid → return error immediately; valid → fall through to the
      // idempotency check against previously-stored *valid* records.
      const validator = TAX_ID_VALIDATORS[input.type];
      const isInvalidLiteral = normalizedValue === 'INVALID';
      const formatOk = !isInvalidLiteral && validator !== undefined && validator.test(normalizedValue);
      if (!formatOk) {
        return { error: 'INVALID_FORMAT' as TaxIdError };
      }

      const records = this.taxIdsByTenant.get(tenantId) ?? [];

      // Idempotent: same (type, value, countryCode) triple returns existing.
      // Only matches against previously-stored valid records (invalids are
      // never persisted, per the format-first check above).
      const existing = records.find(
        r =>
          r.type === input.type &&
          r.value === normalizedValue &&
          r.countryCode === input.countryCode
      );
      if (existing !== undefined) {
        return existing;
      }

      const record: TaxIdRecord = {
        id: `txid_${shortHex(12)}`,
        type: input.type,
        value: normalizedValue,
        countryCode: input.countryCode,
        valid: true,
        createdAt: new Date().toISOString()
      };

      this.taxIdsByTenant.set(tenantId, [...records, record]);
      return record;
    });
  }

  removeTaxId(taxIdRecordId: string): Observable<void> {
    return this.withScenario(scenario => {
      this.ensureTaxIdsSeeded();
      const tenantId = this.resolveTenantId(scenario);
      if (tenantId === null) {
        return undefined;
      }
      const records = this.taxIdsByTenant.get(tenantId);
      if (records === undefined) {
        return undefined;
      }
      const next = records.filter(r => r.id !== taxIdRecordId);
      if (next.length !== records.length) {
        this.taxIdsByTenant.set(tenantId, next);
      }
      return undefined;
    });
  }

  private resolveTenantId(scenario: Scenario): string | null {
    const subscription = this.resolveSubscription(scenario);
    return subscription?.tenantId ?? null;
  }

  private ensureTaxIdsSeeded(): void {
    if (this.taxIdsSeeded) {
      return;
    }
    this.taxIdsSeeded = true;
    // Seed the DE tenant with a valid EU VAT ID for the reverse_charge_de
    // scenario. Lookup the tenantId from the fixture directly so we don't
    // hardcode here.
    const deScenario = SCENARIOS['reverse_charge_de'];
    if (deScenario.subscription !== null) {
      const tenantId = deScenario.subscription.tenantId;
      this.taxIdsByTenant.set(tenantId, [
        {
          id: 'txid_de_seed',
          type: 'eu_vat',
          value: 'DE123456789',
          countryCode: 'DE',
          valid: true,
          createdAt: new Date().toISOString()
        }
      ]);
    }
  }

  private computeTaxPreview(input: TaxPreviewInput): TaxPreview {
    const config = TAX_RATE_TABLE[input.countryCode];
    // Country has no configured rate: treat as 0%, exclusive, "No tax".
    if (config === undefined) {
      return {
        taxableAmountMinor: input.taxableAmountMinor,
        taxAmountMinor: 0,
        taxRate: 0,
        taxBasis: 'exclusive',
        jurisdiction: 'No tax configured',
        label: 'Tax',
        calculation: 'manual'
      };
    }

    // Reverse-charge: customer in eligible country WITH a valid VAT ID of the
    // same country. EU + GB B2B.
    const customerTaxId = input.customerTaxId;
    if (
      customerTaxId !== undefined &&
      REVERSE_CHARGE_COUNTRIES.has(input.countryCode) &&
      customerTaxId.countryCode === input.countryCode
    ) {
      const validator = TAX_ID_VALIDATORS[customerTaxId.type];
      const isValid =
        validator !== undefined && validator.test((customerTaxId.value ?? '').toUpperCase());
      if (isValid) {
        return {
          taxableAmountMinor: input.taxableAmountMinor,
          taxAmountMinor: 0,
          taxRate: 0,
          taxBasis: 'reverse_charge',
          jurisdiction: config.jurisdiction,
          label: config.label,
          calculation: 'reverse_charge',
          reverseChargeNote: `VAT reverse charge applied (${customerTaxId.value.toUpperCase()})`
        };
      }
    }

    // Standard calculation. Inclusive: tax is already inside the listed price;
    // we back it out via banker's rounding. Exclusive: tax is added on top.
    let taxAmountMinor: number;
    let taxBasis: TaxBasis;
    if (config.basis === 'inclusive') {
      const gross = input.taxableAmountMinor;
      const net = bankerRound(gross / (1 + config.rate));
      taxAmountMinor = gross - net;
      taxBasis = 'inclusive';
    } else {
      taxAmountMinor = bankerRound(input.taxableAmountMinor * config.rate);
      taxBasis = 'exclusive';
    }

    return {
      taxableAmountMinor: input.taxableAmountMinor,
      taxAmountMinor,
      taxRate: config.rate,
      taxBasis,
      jurisdiction: config.jurisdiction,
      label: config.label,
      calculation: 'auto'
    };
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private withScenario<T>(project: (scenario: Scenario) => T): Observable<T> {
    const scenario = SCENARIOS[this.activeScenario$.value];
    return of(scenario).pipe(
      delay(randomDelayMs()),
      map(project)
    );
  }

  private resolveSubscription(scenario: Scenario): Subscription | null {
    if (this.subscriptionOverrides.has(scenario.name)) {
      // Allow null overrides (e.g., future cancel flows) by checking presence
      // rather than truthiness.
      return this.subscriptionOverrides.get(scenario.name) ?? null;
    }
    return scenario.subscription;
  }

  private resolveAICreditBalance(scenario: Scenario): AICreditBalance | null {
    if (this.aiCreditBalanceOverrides.has(scenario.name)) {
      return this.aiCreditBalanceOverrides.get(scenario.name) ?? null;
    }
    return scenario.aiCreditBalance;
  }

  private setSubscriptionOverride(name: ScenarioName, subscription: Subscription | null): void {
    this.subscriptionOverrides.set(name, subscription);
    // Re-emit the active scenario name so subscribers to
    // activeScenarioChanges$ re-fetch. We use a fresh emission (next) rather
    // than mutating the BehaviorSubject value because subscribers compare
    // values by reference; the name is unchanged but the underlying data is
    // different.
    this.activeScenario$.next(name);
  }

  private isPromoError<T extends { error: PromotionCodeError } | { error: string }>(
    value: AppliedCoupon | T
  ): value is T {
    return typeof value === 'object' && value !== null && 'error' in (value as object);
  }

  private applyCouponToSubscription(
    subscription: Subscription,
    coupon: AppliedCoupon
  ): Subscription {
    // Recompute discount + total inline. For percent coupons, derive from
    // pricePerSeatMinor * seats; for fixed-amount coupons, use amountOffMinor
    // directly. Tax stays 0 in the prototype (handled in #10).
    const subtotal = subscription.subtotalMinor;
    let discount = 0;
    if (coupon.discountType === 'percent' && coupon.percentOff !== null) {
      discount = Math.round(subtotal * (coupon.percentOff / 100));
    } else if (coupon.discountType === 'fixed' && coupon.amountOffMinor !== null) {
      discount = Math.min(coupon.amountOffMinor, subtotal);
    }
    const total = Math.max(0, subtotal - discount + subscription.taxMinor);
    return {
      ...subscription,
      appliedCoupon: coupon,
      discountMinor: discount,
      totalMinor: total
    };
  }

  private clearCouponFromSubscription(subscription: Subscription): Subscription {
    return {
      ...subscription,
      appliedCoupon: null,
      discountMinor: 0,
      totalMinor: subscription.subtotalMinor + subscription.taxMinor
    };
  }

  private findPricePoint(
    plans: Plan[],
    tier: string,
    countryCode: string,
    currency: string,
    billingPeriod: string
  ): PricePoint | null {
    const plan = plans.find(p => p.code === tier);
    if (plan === undefined) {
      return null;
    }
    const match = plan.pricing.find(
      p =>
        p.countryCode === countryCode &&
        p.currency === currency &&
        p.billingPeriod === billingPeriod
    );
    return match ?? null;
  }

  private computePromotionCodeResult(
    subscription: Subscription | null,
    rawCode: string,
    options: { isPreview: boolean }
  ): AppliedCoupon | { error: PromotionCodeError } {
    const code = (rawCode ?? '').trim().toUpperCase();
    const nowIso = new Date().toISOString();
    const inThreeMonths = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();

    // Re-applying a code that is already attached to the active subscription
    // is an explicit error per #02 (PROMOTION_CODE_ALREADY_APPLIED). We only
    // surface this on commit; preview callers (signup price preview) should
    // be able to re-validate freely.
    if (
      !options.isPreview &&
      subscription !== null &&
      subscription.appliedCoupon !== null &&
      subscription.appliedCoupon.promotionCode.toUpperCase() === code
    ) {
      return { error: 'PROMOTION_CODE_ALREADY_APPLIED' };
    }

    switch (code) {
      case 'LAUNCH20':
        return {
          promotionCode: 'LAUNCH20',
          couponId: 'coupon_launch20',
          name: '20% off first 3 months',
          discountType: 'percent',
          percentOff: 20,
          amountOffMinor: null,
          duration: 'repeating',
          durationInMonths: 3,
          appliedAt: nowIso,
          expiresAt: inThreeMonths
        };
      case 'FOUNDER10':
        return {
          promotionCode: 'FOUNDER10',
          couponId: 'coupon_founder10',
          name: '10% off forever',
          discountType: 'percent',
          percentOff: 10,
          amountOffMinor: null,
          duration: 'forever',
          durationInMonths: null,
          appliedAt: nowIso,
          expiresAt: null
        };
      case 'EXPIRED':
        return { error: 'PROMOTION_CODE_EXPIRED' };
      case 'MAXED':
        return { error: 'PROMOTION_CODE_REDEMPTION_LIMIT_REACHED' };
      case 'DEACTIVATED':
        return { error: 'PROMOTION_CODE_DEACTIVATED' };
      case 'NGN-ONLY': {
        const currency = subscription?.currency ?? null;
        if (currency !== 'NGN') {
          return { error: 'PROMOTION_CODE_CURRENCY_MISMATCH' };
        }
        return {
          promotionCode: 'NGN-ONLY',
          couponId: 'coupon_ngn_only',
          name: 'NGN-only launch discount',
          discountType: 'percent',
          percentOff: 15,
          amountOffMinor: null,
          duration: 'once',
          durationInMonths: null,
          appliedAt: nowIso,
          expiresAt: inThreeMonths
        };
      }
      default:
        return { error: 'PROMOTION_CODE_NOT_FOUND' };
    }
  }
}
